import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, ChevronRight, ArrowUpRight } from "lucide-react";
import { company } from "../data/company";
import { serviceCategories, services } from "../data/services";
import { cities } from "../data/cities";
import { useConsent } from "../context/ConsentContext";
import { trackClick } from "../utils/analytics";

export const Footer = () => {
  const { revoke } = useConsent();
  return (
    <footer className="mt-20 md:mt-28" data-testid="main-footer" style={{ background: "#08182A", color: "#FFFFFF" }}>
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-20">
        {/* Top CTA strip — orange highlight card */}
        <div className="rounded-[28px] p-8 md:p-12 mb-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" style={{ background: "linear-gradient(135deg, #B8924A 0%, #A07F3F 100%)" }}>
          <div>
            <div className="text-xs font-semibold tracking-[0.18em] uppercase text-white/85 mb-3">Klaar voor de volgende stap</div>
            <h3 className="text-2xl md:text-3xl lg:text-4xl text-white font-bold leading-tight tracking-tight">
              Vraag uw vrijblijvende offerte aan
            </h3>
          </div>
          <Link
            to="/offerte"
            className="inline-flex items-center gap-2 bg-white text-[#08182A] font-semibold px-6 py-3.5 rounded-full hover:bg-white/90 transition-all hover:translate-y-[-2px] whitespace-nowrap shadow-soft"
            data-testid="footer-cta-offerte"
          >
            Offerte aanvragen <ChevronRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="/brand/logo-white.svg"
                alt="Koning Aannemersbedrijf"
                loading="lazy"
                className="h-14 w-auto"
              />
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-md mt-4">
              Premium aannemer voor renovaties en verbouwingen in heel Zuid-Holland. Vast uitvoeringsteam, vaste prijs, vakkundig werk.
            </p>
            <div className="mt-6 space-y-3">
              <a href={company.phoneHref} onClick={() => trackClick("phone", "footer")} className="flex items-center gap-3 text-white hover:text-[#B8924A] transition-colors text-sm" data-testid="footer-phone">
                <Phone size={15} style={{ color: "#B8924A" }} /> {company.phone}
              </a>
              <a href={company.emailHref} className="flex items-center gap-3 text-white hover:text-[#B8924A] transition-colors text-sm" data-testid="footer-email">
                <Mail size={15} style={{ color: "#B8924A" }} /> {company.email}
              </a>
              <div className="flex items-start gap-3 text-white/70 text-sm">
                <MapPin size={15} style={{ color: "#B8924A" }} className="mt-0.5" />
                <span>
                  {company.address.street && <>{company.address.street}<br /></>}
                  {company.address.postalCode} {company.address.city}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.18em] uppercase font-bold mb-5" style={{ color: "#B8924A" }}>Diensten</h4>
            <ul className="space-y-2.5">
              {serviceCategories.map((c) => (
                <li key={c.slug}>
                  <Link to={`/diensten#${c.slug}`} className="text-sm text-white/70 hover:text-white transition-colors">
                    {c.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/diensten" className="text-sm font-semibold hover:underline inline-flex items-center gap-1" style={{ color: "#B8924A" }}>Alle diensten <ArrowUpRight size={12} /></Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.18em] uppercase font-bold mb-5" style={{ color: "#B8924A" }}>Populaire pagina&apos;s</h4>
            <ul className="space-y-2.5">
              {["badkamer-renovatie", "keuken-renovatie", "aanbouw", "dakkapel", "kunststof-kozijnen", "stukadoor"].map((slug) => {
                const s = services.find((x) => x.slug === slug);
                if (!s) return null;
                return (
                  <li key={slug}>
                    <Link to={`/diensten/${slug}`} className="text-sm text-white/70 hover:text-white transition-colors">{s.title}</Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.18em] uppercase font-bold mb-5" style={{ color: "#B8924A" }}>Werkgebied</h4>
            <ul className="space-y-2.5">
              {cities.map((c) => (
                <li key={c.slug}>
                  <Link to={`/locaties/${c.slug}`} className="text-sm text-white/70 hover:text-white transition-colors">{c.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/60">
            <span>© {new Date().getFullYear()} {company.name}</span>
            <span>KvK {company.kvk}</span>
            <span>BTW {company.btw}</span>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/algemene-voorwaarden" className="hover:text-white transition-colors">Algemene Voorwaarden</Link>
            <Link to="/gratis-checklist" className="hover:underline font-semibold" style={{ color: "#B8924A" }} data-testid="footer-link-checklist">Gratis checklist</Link>
            <Link to="/kennisbank" className="hover:text-white transition-colors" data-testid="footer-link-kennisbank">Kennisbank</Link>
            <button onClick={revoke} className="hover:text-white transition-colors" data-testid="footer-cookie-revoke" type="button">Cookie-voorkeuren</button>
          </div>
          <div className="flex items-center gap-3">
            <a href={company.social.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center text-white/70 hover:text-[#08182A] hover:bg-[#B8924A] hover:border-[#B8924A] transition-all" aria-label="Instagram"><Instagram size={16} /></a>
            <a href={company.social.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center text-white/70 hover:text-[#08182A] hover:bg-[#B8924A] hover:border-[#B8924A] transition-all" aria-label="Facebook"><Facebook size={16} /></a>
            <a href={company.social.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center text-white/70 hover:text-[#08182A] hover:bg-[#B8924A] hover:border-[#B8924A] transition-all" aria-label="LinkedIn"><Linkedin size={16} /></a>
          </div>
        </div>
      </div>
    </footer>
  );
};
